// 🧩 مكونات واجهة الشريك (ارت تاتش) - غلاف الصفحة + الهيدر + الكروت +
// الحقول + كروت الأرقام. عرض فقط، كل البيانات والمنطق تبقى بالصفحات نفسها.
import React from "react";
import { ThemeProvider } from "@mui/material/styles";
import { Box, Typography, TextField } from "@mui/material";
import partnerTheme, { P } from "./partnerTheme";
import "../styles/artika-fonts.css";

// غلاف كل صفحات الشريك: الثيم + خلفية الصفحة + عرض موحّد
export const PartnerShell = ({ children, maxWidth = 1180 }) => (
  <ThemeProvider theme={partnerTheme}>
    <Box
      aria-hidden
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: -1,
        background: P.bg,
      }}
    />
    <Box
      dir="rtl"
      sx={{
        fontFamily: P.font,
        color: P.text,
        width: "100%",
        maxWidth,
        mx: "auto",
        mt: { xs: -1, md: 0 },
        pb: 8,
        "& *": { fontFamily: "inherit" },
      }}
    >
      {children}
    </Box>
  </ThemeProvider>
);

// هيدر الصفحة: عنوان صغير (eyebrow) + عنوان + وصف + أزرار على الطرف
export const PageHeader = ({ eyebrow, title, subtitle, actions, back }) => (
  <Box sx={{ mb: { xs: 3, md: 4 } }}>
    {back}
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "flex-end" },
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box>
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <Typography component="h1" sx={{ fontSize: { xs: 26, md: 32 }, fontWeight: 800, lineHeight: 1.25, letterSpacing: "-0.01em" }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ color: P.muted, mt: 0.75, fontSize: 15, maxWidth: 560, lineHeight: 1.7 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>{actions}</Box>}
    </Box>
  </Box>
);

export const Eyebrow = ({ children, color = P.gold }) => (
  <Typography
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1,
      fontSize: 12.5,
      fontWeight: 700,
      color,
      mb: 1,
      "&::before": { content: '""', width: 18, height: 2, borderRadius: 2, background: color },
    }}
  >
    {children}
  </Typography>
);

export const Card = ({ children, sx, ...rest }) => (
  <Box
    sx={{
      background: P.surface,
      border: `1px solid ${P.line}`,
      borderRadius: `${P.radius + 4}px`,
      boxShadow: P.shadow,
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);

// عنوان قسم داخل كرت: أيقونة + عنوان + وصف + عنصر بالطرف
export const SectionHead = ({ icon: Icon, title, hint, end, sx }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5, ...sx }}>
    {Icon && (
      <Box sx={{ width: 40, height: 40, borderRadius: "12px", display: "grid", placeItems: "center", background: P.goldSoft, color: P.gold, flexShrink: 0 }}>
        <Icon size={20} strokeWidth={1.8} />
      </Box>
    )}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 800, fontSize: 17 }}>{title}</Typography>
      {hint && <Typography sx={{ color: P.muted, fontSize: 13, mt: 0.25 }}>{hint}</Typography>}
    </Box>
    {end}
  </Box>
);

// حقل بعنوان ثابت فوكه (بدل العنوان العائم - أوضح ويناسب الاتجاه العربي)
export const Field = ({ label, optional, children, sx, ...props }) => (
  <Box sx={sx}>
    {label && (
      <Typography component="label" sx={{ display: "block", fontSize: 13.5, fontWeight: 700, color: P.ink2, mb: 0.75 }}>
        {label}
        {optional && <Box component="span" sx={{ color: P.faint, fontWeight: 400, mr: 0.75 }}>(اختياري)</Box>}
      </Typography>
    )}
    <TextField fullWidth {...props}>{children}</TextField>
  </Box>
);

// لاحقة داخل الحقل (سم، د.ع، %)
export const Suffix = ({ children }) => (
  <Box component="span" sx={{ color: P.faint, fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", pl: 0.5 }}>
    {children}
  </Box>
);

// مبلغ بالدينار: الرقم كبير + "د.ع" صغيرة
export const Money = ({ value, size = 22, color = "inherit", weight = 800, unitColor }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.6, color, direction: "rtl" }}>
    <Box component="span" sx={{ fontSize: size, fontWeight: weight, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em", direction: "ltr" }}>
      {Math.round(Number(value) || 0).toLocaleString("en-US")}
    </Box>
    <Box component="span" sx={{ fontSize: Math.max(11, size * 0.5), fontWeight: 700, color: unitColor || P.faint }}>د.ع</Box>
  </Box>
);

export const StatCard = ({ icon: Icon, label, children, foot, tone = "light" }) => {
  const dark = tone === "dark";
  return (
    <Card
      sx={{
        p: { xs: 2.25, md: 2.75 },
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        ...(dark && { background: P.ink, color: "#fff", borderColor: P.ink }),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: dark ? "rgba(255,255,255,.7)" : P.muted }}>{label}</Typography>
        {Icon && (
          <Box sx={{ width: 34, height: 34, borderRadius: "10px", display: "grid", placeItems: "center", background: dark ? "rgba(255,255,255,.08)" : P.surface2, color: dark ? P.goldLine : P.ink2, border: `1px solid ${dark ? "rgba(255,255,255,.1)" : P.line}` }}>
            <Icon size={17} strokeWidth={1.8} />
          </Box>
        )}
      </Box>
      <Box sx={{ mt: "auto" }}>{children}</Box>
      {foot && <Box sx={{ fontSize: 12.5, color: dark ? "rgba(255,255,255,.6)" : P.muted }}>{foot}</Box>}
    </Card>
  );
};

export const Pill = ({ children, tone = "neutral", icon: Icon, sx }) => {
  const tones = {
    neutral: { bg: P.surface2, fg: P.ink2, bd: P.line },
    gold: { bg: P.goldSoft, fg: "#7a5d2e", bd: P.goldLine },
    success: { bg: P.successBg, fg: P.success, bd: "#cfe6d9" },
    warning: { bg: P.warningBg, fg: P.warning, bd: "#f1dfb9" },
    danger: { bg: P.dangerBg, fg: P.danger, bd: "#f3cfcc" },
  }[tone];
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, px: 1.1, py: 0.35, borderRadius: 99, fontSize: 12.5, fontWeight: 700, background: tones.bg, color: tones.fg, border: `1px solid ${tones.bd}`, whiteSpace: "nowrap", ...sx }}>
      {Icon && <Icon size={13} strokeWidth={2} />}
      {children}
    </Box>
  );
};

export const EmptyState = ({ icon: Icon, title, hint, action }) => (
  <Box sx={{ textAlign: "center", py: 7, px: 2, border: `1.5px dashed ${P.lineStrong}`, borderRadius: `${P.radius + 4}px`, background: "rgba(255,255,255,.6)" }}>
    {Icon && (
      <Box sx={{ width: 56, height: 56, mx: "auto", mb: 2, borderRadius: "16px", display: "grid", placeItems: "center", background: P.goldSoft, color: P.gold }}>
        <Icon size={26} strokeWidth={1.6} />
      </Box>
    )}
    <Typography sx={{ fontWeight: 800, fontSize: 17 }}>{title}</Typography>
    {hint && <Typography sx={{ color: P.muted, fontSize: 14, mt: 0.5 }}>{hint}</Typography>}
    {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
  </Box>
);

export const Skeleton = ({ h = 20, w = "100%", r = 10, sx }) => (
  <Box
    sx={{
      height: h,
      width: w,
      borderRadius: `${r}px`,
      background: `linear-gradient(90deg, ${P.line} 0%, ${P.surface2} 50%, ${P.line} 100%)`,
      backgroundSize: "200% 100%",
      animation: "partnerShimmer 1.4s ease-in-out infinite",
      "@keyframes partnerShimmer": { "0%": { backgroundPosition: "100% 0" }, "100%": { backgroundPosition: "-100% 0" } },
      ...sx,
    }}
  />
);
